import { useEffect, useMemo, useState } from 'react';

import {
  useAssignLeadTag,
  useCreateLeadTag,
  useLeadTags,
  useUnassignLeadTag,
} from '@barel/api-client/react';
import { useT } from '@barel/i18n';
import { Button, Input, Pill, Select, toast } from '@barel/ui';

export interface LeadTagsEditorProps {
  leadId: string;
  enabled: boolean;
}

export function LeadTagsEditor({ leadId, enabled }: LeadTagsEditorProps) {
  const t = useT('leads');
  const tagsQ = useLeadTags(leadId, { enabled });
  const createTag = useCreateLeadTag();
  const assignTag = useAssignLeadTag();
  const unassignTag = useUnassignLeadTag();
  const [selectedTagId, setSelectedTagId] = useState('');
  const [newName, setNewName] = useState('');

  useEffect(() => {
    setSelectedTagId('');
    setNewName('');
  }, [leadId]);

  const assignedIds = useMemo(
    () => new Set(tagsQ.data?.assignedTagIds ?? []),
    [tagsQ.data?.assignedTagIds],
  );
  const assigned = (tagsQ.data?.tags ?? []).filter((tag) => assignedIds.has(tag.id));
  const available = (tagsQ.data?.tags ?? []).filter((tag) => !assignedIds.has(tag.id));
  const pending = createTag.isPending || assignTag.isPending || unassignTag.isPending;

  const assign = async (tagId: string) => {
    try {
      await assignTag.mutateAsync({ leadId, tagId });
      setSelectedTagId('');
    } catch {
      toast.error({ title: t('detail.tagsError') });
    }
  };

  const createAndAssign = async () => {
    const name = newName.trim();
    if (!name || name.length > 40) return;
    try {
      const created = await createTag.mutateAsync({ name, color: '#5b6ef8' });
      await assignTag.mutateAsync({ leadId, tagId: created.tag.id });
      setNewName('');
    } catch {
      toast.error({ title: t('detail.tagsError') });
    }
  };

  return (
    <section className="flex flex-col gap-2" aria-label={t('detail.tagsLabel')}>
      <span className="text-xs text-tertiary">{t('detail.tagsLabel')}</span>

      {assigned.length > 0 ? (
        <div className="flex flex-wrap gap-2" data-testid="lead-tags-assigned">
          {assigned.map((tag) => (
            <Pill
              key={tag.id}
              tone="neutral"
              variant="soft"
              size="sm"
              icon={
                <span
                  aria-hidden="true"
                  className="inline-block size-2 rounded-full"
                  style={{ backgroundColor: tag.color }}
                />
              }
            >
              <span>{tag.name}</span>
              <button
                type="button"
                className="ml-1 rounded px-0.5 text-tertiary hover:text-primary focus-visible:shadow-ring-focus"
                aria-label={t('detail.removeTag', { name: tag.name })}
                disabled={pending}
                onClick={() => {
                  void unassignTag
                    .mutateAsync({ leadId, tagId: tag.id })
                    .catch(() => toast.error({ title: t('detail.tagsError') }));
                }}
              >
                ×
              </button>
            </Pill>
          ))}
        </div>
      ) : (
        <span className="text-xs text-tertiary">{t('detail.noTags')}</span>
      )}

      {available.length > 0 ? (
        <div className="flex items-center gap-2">
          <Select value={selectedTagId} onValueChange={setSelectedTagId}>
            <Select.Trigger size="sm" aria-label={t('detail.addExistingTag')}>
              <Select.Value placeholder={t('detail.addExistingTag')} />
            </Select.Trigger>
            <Select.Portal>
              <Select.Content layer="modal">
                {available.map((tag) => (
                  <Select.Item key={tag.id} value={tag.id}>
                    {tag.name}
                  </Select.Item>
                ))}
              </Select.Content>
            </Select.Portal>
          </Select>
          <Button
            variant="ghost"
            size="sm"
            disabled={!selectedTagId || pending}
            onClick={() => void assign(selectedTagId)}
          >
            {t('detail.addTag')}
          </Button>
        </div>
      ) : null}

      <div className="flex items-center gap-2">
        <Input
          value={newName}
          maxLength={40}
          aria-label={t('detail.newTagPlaceholder')}
          placeholder={t('detail.newTagPlaceholder')}
          onChange={(event) => setNewName(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              event.preventDefault();
              void createAndAssign();
            }
          }}
        />
        <Button
          variant="ghost"
          size="sm"
          disabled={!newName.trim() || pending}
          onClick={() => void createAndAssign()}
        >
          {t('detail.createTag')}
        </Button>
      </div>

      {tagsQ.isError ? <span className="text-xs text-error">{t('detail.tagsError')}</span> : null}
    </section>
  );
}
